import type { ViewRecord } from "@/shared/domain/views";

import { staticFilterFieldsFor } from "../Filters/catalog";
import type { UseResourceViewOptions } from "../useResourceView";
import { definitionToViewState } from "../viewSerialization";

/**
 * Merge a View into the options a list hands to `useResourceView`, so the
 * table starts out showing the View instead of starting empty and being
 * corrected a moment later. `useResourceView` reads these options once, on
 * mount, so this is the one chance to start the table in the right state.
 *
 * Returns `null` when the View cannot be restored synchronously, and the
 * ordinary resolve-then-apply path should own the navigation instead:
 *
 * - Written under a scheme this client does not read. `useApplyView` raises
 *   the same failure on the apply path, which is where the customer is told.
 * - A RoboQL View. Restoring one means turning its query into filters, and
 *   only the server can do that.
 * - A View with no filters. `useLoadedView` holds "a table showing no filters
 *   has no View on it" as an invariant, so seeding one would flicker in and
 *   straight back out. One should not exist; the Save control only renders
 *   alongside filters.
 *
 * A View that recorded no sort, paging or columns defers to the list's own
 * defaults rather than blanking them: `null` means "unstated", not "none",
 * the same rule `useApplyView` follows.
 */
export function seedResourceViewOptions<TData>(
  record: ViewRecord,
  options: UseResourceViewOptions<TData>,
): UseResourceViewOptions<TData> | null {
  let restored;
  try {
    restored = definitionToViewState(
      record.definition,
      staticFilterFieldsFor(record.target),
    );
  } catch {
    return null;
  }

  const { filters, sort, pageSize, visibleColumns } = restored;
  if (filters.type !== "structured") {
    return null;
  }

  return {
    ...options,
    initialActiveFilters: filters,
    initialSort: sort ?? options.initialSort,
    preferences: {
      columns: visibleColumns ?? options.preferences?.columns,
      rowsPerPage: pageSize ?? options.preferences?.rowsPerPage,
    },
  };
}
